/**
 * Registration / RTO workflow — display stages.
 *
 * Per ops spec, the previously-detailed Road Tax / HSRP / Submitted / Pending
 * labels collapse into ONLY THREE stages for end users:
 *
 *   1. road_tax_pending      — Submitted to RTO = No
 *   2. awaiting_rto_approval — Submitted to RTO = Yes, no registration_no yet
 *   3. registration_completed — registration_no captured
 *
 * Audit trail and underlying tasks (ROAD_TAX + HSRP) are unchanged; this is
 * purely the surfaced label across Sales List + Sales Detail + workflow stepper.
 */
export const REGISTRATION_STAGE = {
  pending: {
    key: "road_tax_pending",
    label: "Road Tax Pending",
    tone: "amber",
    cls:  "border-amber-200 bg-amber-50 text-amber-800",
    dot:  "bg-amber-500",
    icon: "amber",
    sub:  "Awaiting submission to RTO",
  },
  awaiting: {
    key: "awaiting_rto_approval",
    label: "Awaiting RTO Approval",
    tone: "indigo",
    cls:  "border-indigo-200 bg-indigo-50 text-indigo-800",
    dot:  "bg-indigo-500",
    icon: "indigo",
    sub:  "Submitted to RTO · awaiting approval",
  },
  done: {
    key: "registration_completed",
    label: "Registration Completed",
    tone: "emerald",
    cls:  "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot:  "bg-emerald-500",
    icon: "emerald",
    sub:  "Registration number generated",
  },
};

/**
 * Resolve the current registration stage for a sale.
 *
 * Priority:
 *   1. `road_tax.rto_stage === 'completed'` → registration is officially done.
 *   2. A captured registration number on `registration.regn_no` (the schema
 *      field — note: NOT `registration_no`).
 *   3. Otherwise: submitted-to-rto vs. pending.
 *
 * @param {object} sale — full sale document from /api/sales/:id
 * @returns {{stage, regNo: string|null}} stage meta + captured reg no (if any).
 */
export function getRegistrationStage(sale) {
  const rt = sale?.road_tax || {};
  const regNo = (sale?.registration?.regn_no || rt.regn_no || "").trim();
  if (rt.rto_stage === "completed" || regNo) {
    return { stage: REGISTRATION_STAGE.done, regNo: regNo || null };
  }
  const submitted = !!rt.submitted_to_rto;
  return {
    stage: submitted ? REGISTRATION_STAGE.awaiting : REGISTRATION_STAGE.pending,
    regNo: null,
  };
}

/**
 * Same helper, but from a Tasks-array projection (used by the Sales List queue
 * row where we don't fetch the full sale). Returns the same stage object so
 * the caller can render with the same classes.
 *
 * Falls back to "done" only when the row carries an explicit registration_no
 * (the legacy projection on /api/sales already includes it on `sale.registration`).
 */
export function getRegistrationStageFromTasks(tasks, sale) {
  return getRegistrationStage(sale || {});
}
